import i18next from "i18next";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import Ic_print from "../../../../assets/images/icons/print_black.svg";
import { GeneralButton, Table } from "../../../../components";
import { EndPoints } from "../../../../Constants/endpoints";
import { MedicalKeys } from "../../../../enums/MedicalReportEnums";
import { useGetPersonalInfo, usePostData } from "../../../../hooks";
import { IStep, IStepFields } from "../../../../interface/MedicalRecord";
import { RootState } from "../../../../redux/store";
import { handlePrint } from "../../../../utils/fileUtils";
import { formatAge, formatDoctorName } from "../../../../utils/utils";
import { recordTablesData } from "../recordDetailsData";
import { MedicalRecordStep } from "./MedicalRecordStep";

const PatientMedicalRecordBody = ({
  GetMedicalRecord,
  className,
  TemplateData,
  type,
}: any) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const stepParam = searchParams.get("step"); 
  const { data, isLoading } = GetMedicalRecord;
  const [isPrinting, setIsPrinting] = useState(false); 
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const { data: personalData } = useGetPersonalInfo();

  const UserInfo = useSelector((state: RootState) => state.registration);

  const TreatmentsData = data?.Data?.Treatments;
  const drugsTable: any = recordTablesData?.find(
    (item) => item?.key == MedicalKeys.Medications,
  );

  const { mutate, isPending } = usePostData(
    `${EndPoints.PrintPrescription}/${TreatmentsData?.Id}`,
  );

  const selectedStepFields = TemplateData?.Data?.Steps?.find((item: IStep) => {
    return item?.Id == Number(stepParam);
  })?.StepFields?.map((field: IStepFields) => field?.Key);

  const highlightedFieldsKey = selectedStepFields?.join("|") ?? "";

  useEffect(() => {
    if (isLoading || !stepParam) return;

    let timeoutId: number;
    let attempts = 0;

    const scrollToHighlightedSection = () => {
      attempts += 1;

      const container = scrollContainerRef.current;

      if (!container) {
        if (attempts < 15) {
          timeoutId = window.setTimeout(scrollToHighlightedSection, 100);
        }
        return;
      }

      const target = container.querySelector<HTMLElement>(
        "[data-highlighted-section='true'], .border-orange",
      );

      if (!target) {
        if (attempts < 15) {
          timeoutId = window.setTimeout(scrollToHighlightedSection, 100);
        }
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();

      const top = targetRect.top - containerRect.top + container.scrollTop - 24;

      container.scrollTo({
        top: Math.max(top, 0),
        behavior: "smooth",
      });
    };

    timeoutId = window.setTimeout(scrollToHighlightedSection, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isLoading, stepParam, highlightedFieldsKey]);

  

  const patientInfo = [
    {
      label: t("patient"),
      value: data?.Data?.Patient?.Patient?.FullName,
    },
    {
      label: t("phone_number"),
      value: data?.Data?.Patient?.Patient?.PhoneNumber
        ? `${UserInfo.userDetails.Country?.Code}-${data?.Data?.Patient?.Patient?.PhoneNumber}`
        : null,
      dir: "ltr",
    },
    {
      label: t("birthday"),
      value: data?.Data?.Birthday ?? data?.Data?.Patient?.Patient?.Birthday,
    },
    {
      label: t("age"),
      value:
        (data?.Data?.Birthday || data?.Data?.Patient?.Patient?.Birthday) &&
        formatAge((data?.Data?.Birthday ?? data?.Data?.Patient?.Patient?.Birthday) || "", true) + " " + t("years_old"),
    },
    {
      label: t("weight"),
      value: (data?.Data?.Weight || data?.Data?.Patient?.Weight)
        ? (data?.Data?.Weight ?? data?.Data?.Patient?.Weight) + " " + t("kg")
        : undefined,
    },
    {
      label: t("length"),
      value: data?.Data?.Length || data?.Data?.Patient?.Height
        ? (data?.Data?.Length ?? data?.Data?.Patient?.Height) + " " + t("cm")
        : undefined,
    },
  ];


  return (
    <>
      {isLoading ? (
        <div className="mt-2">
          <div className="mb-2 h-20 w-full animate-pulse rounded-md bg-gray-200" />
          <div className="mb-2 h-24 w-full animate-pulse rounded-md bg-gray-200" />
        </div>
      ) : (
        <div
          ref={scrollContainerRef}
          className={`max-h-[calc(100dvh-7rem)] overflow-auto pb-3 ${className}`}
        >
          <div className={`flex flex-col gap-4`}>
            <div className="grid grid-cols-3 gap-2 rounded-lg border border-[#919EAB33] bg-Gray p-6 text-start">
              {patientInfo
                .filter(
                  (item) =>
                    item.value !== null &&
                    item.value !== undefined &&
                    item.value !== "",
                )
                .map((item, index) => (
                  <div className="flex gap-2" key={index}>
                    <h2 className="font-semibold">{item.label} :</h2>
                    <p dir={item.dir}>{item.value}</p>
                  </div>
                ))}
            </div>

            {(TreatmentsData?.TreatmentPlan ||
              TreatmentsData?.Drugs?.length > 0) && (
              <div
                data-highlighted-section={
                  selectedStepFields?.includes(MedicalKeys?.Treatments)
                    ? "true"
                    : undefined
                }
                className={`rounded-lg border border-[#919EAB33] bg-Gray p-6 ${
                  selectedStepFields?.includes(MedicalKeys?.Treatments)
                    ? "border-orange !bg-[#FFB0001A]"
                    : ""
                }`}
              >
                <div className="flex justify-between">
                  {TreatmentsData?.CreatedAt && (
                    <h2 className="font-bold">
                      {t("treatment_plan")} - {TreatmentsData?.CreatedAt}
                    </h2>
                  )}

                  {type !== "preview" && TreatmentsData?.Drugs?.length > 0 && (
                    <GeneralButton
                      title={t("print_prescription")}
                      color="lite"
                      className="border-body-text !text-primary-text border !bg-white"
                      icon={Ic_print}
                      isLoading={isPending && isPrinting}
                      onClick={() => {
                        setIsPrinting(true);
                        mutate(
                          {},
                          {
                            onSuccess: (data) => {
                              handlePrint(
                                data?.Data?.FileContents,
                                data?.Data?.FileDownloadName,
                                setIsPrinting,
                              );
                            },
                          },
                        );
                      }}
                    />
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  {TreatmentsData?.TreatmentPlan && (
                    <>
                      <h2>{t("treatment_plan")}</h2>
                      <div
                        className="rounded-lg bg-white p-2"
                        dangerouslySetInnerHTML={{
                          __html: TreatmentsData?.TreatmentPlan,
                        }}
                      />
                    </>
                  )}

                  {TreatmentsData?.Drugs?.length > 0 && (
                    <div className="mt-2 flex flex-col gap-1">
                      <h2>{t("treatments")}</h2>
                      <div
                        className={`rounded-lg border border-[#919EAB33] bg-Gray p-6 ${
                          drugsTable?.columns?.length > 3 ? "col-span-2" : ""
                        }`}
                      >
                        <Table
                          headerTitle={t(drugsTable?.title)}
                          data={TreatmentsData?.Drugs}
                          columns={drugsTable?.columns}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="grid gap-2 md:grid-cols-2">
              {data?.Data?.Attributes?.map((step: any, index: number) => {
                return (
                  step?.IsVisible && (
                    <MedicalRecordStep
                      key={index}
                      step={step}
                      data={data?.Data}
                    />
                  )
                );
              })}
            </div>

            <div className="grid gap-2 md:grid-cols-2">
              {recordTablesData?.map((table, index) => {
                const isHighlighted = selectedStepFields?.includes(table?.key);

                return data?.Data?.[table?.key] ? (
                  <div
                    key={index}
                    data-highlighted-section={
                      isHighlighted ? "true" : undefined
                    }
                    className={`rounded-lg border border-[#919EAB33] bg-Gray p-6 ${
                      table?.columns?.length > 3 ? "col-span-2" : ""
                    } ${isHighlighted ? "border-orange !bg-[#FFB0001A]" : ""}`}
                  >
                    <Table
                      headerTitle={t(table?.title)}
                      data={data?.Data?.[table?.key]}
                      columns={table?.columns}
                    />
                  </div>
                ) : (
                  <></>
                );
              })}
            </div>

            {!isLoading ? (
              <div
                className={`mb-10 grid grid-cols-3 rounded-lg border border-[#919EAB33] bg-Gray p-4`}
              >
                <div className="flex flex-col gap-2">
                  <p className="text-body-text">{t("report_generated")}</p>
                  <p>
                    {moment(data?.Data?.MedicalHistoryDate, "DD-MM-YYYY")
                      .locale(i18next?.language?.slice(0, 2))
                      .format("MMMM DD, YYYY")}
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <p className="text-body-text">{t("attending_physician")}</p>
                  <p>
                    {formatDoctorName(
                      personalData?.Data?.FullName,
                      personalData?.Data?.FullNameAr,
                      personalData?.Data?.FullNameEn,
                    )}
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-2 h-20 w-full animate-pulse rounded-md bg-gray-200" />
                <div className="mb-2 h-24 w-full animate-pulse rounded-md bg-gray-200" />
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PatientMedicalRecordBody;
